/**
 * How many reports wait — beside «Report triage» for the delivery team, and beside «My reports» for
 * their author (bata-platform#686, ADR 9063; the lifecycle of bata-platform ADR 1020, ADR 9071).
 *
 * From `GET /me/work-counts`, the read the rail's «My work» badge already makes and shares a cache
 * entry with:
 *
 *   · `feedback.received` — reports nobody has taken up — and, for an approver only,
 *     `feedback.ready` — reports ready and waiting for approval: the triage badge counts both and
 *     says each in words for a screen reader;
 *   · `feedback.needsMyReply` — the person's own reports on which the team asked something: the
 *     «My reports» badge.
 *
 * A count the platform does not state — `null`, because the person may not see it
 * (`feedbackUnavailableReason: not_granted`), or no `feedback` at all because the store could not
 * be counted — draws no badge, and never a zero: "nothing waits" and
 * "not yours to see" are different answers. The chip carries its figure in ink on a plain ground and
 * says what it counts in words for a screen reader, so no colour carries the meaning (§12.6).
 */

import { QueryClientContext } from '@tanstack/react-query';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import type { FeedbackCounts, WorkCounts } from '@/api/generated';
import { formatNumber } from '@/lib/format';

import { useWorkCounts } from '../routes/my-work/work-counts';

/**
 * The feedback counts. Since contract 0.101 a person who does not triage gets an object too — their
 * `needsMyReply`, with `received` and `ready` null and `feedbackUnavailableReason: not_granted` — so a
 * null member, not a null object, is what «not yours to see» looks like.
 */
function feedbackCounts(counts: WorkCounts | undefined): FeedbackCounts | null {
  return counts?.feedback ?? null;
}

const positive = (value: unknown): number | null => (typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null);

/** The number of reports waiting for the team, or `null` when the platform states none. Pure, for the test. */
export function receivedReports(counts: WorkCounts | undefined): number | null {
  return positive(feedbackCounts(counts)?.received);
}

/** Reports ready and waiting for approval — sent to approvers only. */
export function readyReports(counts: WorkCounts | undefined): number | null {
  return positive(feedbackCounts(counts)?.ready);
}

/** The person's own reports waiting for their reply. */
export function reportsAwaitingMe(counts: WorkCounts | undefined): number | null {
  return positive(feedbackCounts(counts)?.needsMyReply);
}

/** Drawn only under a query client: the chrome is also drawn on its own, and a missing count is no count. */
export function TriageBadge(): React.JSX.Element | null {
  return useContext(QueryClientContext) === undefined ? null : <CountedBadge which="triage" />;
}

/** The «My reports» badge: the person's reports on which the team asked something. */
export function MyReportsBadge(): React.JSX.Element | null {
  return useContext(QueryClientContext) === undefined ? null : <CountedBadge which="mine" />;
}

function CountedBadge({ which }: { readonly which: 'triage' | 'mine' }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const counts = useWorkCounts().data;
  let count: number | null;
  let label: string;
  if (which === 'mine') {
    count = reportsAwaitingMe(counts);
    label = t('feedback.reports.badgeLabel', { count: count ?? 0 });
  } else {
    const received = receivedReports(counts);
    const ready = readyReports(counts);
    count = received === null && ready === null ? null : (received ?? 0) + (ready ?? 0);
    label = ready === null
      ? t('feedback.triage.badgeLabel', { count: received ?? 0 })
      : t('feedback.triage.badgeLabelWithReady', { count: received ?? 0, ready });
  }
  if (count === null) return null;
  return (
    <span
      data-triage-badge={which === 'triage' ? '' : undefined}
      data-my-reports-badge={which === 'mine' ? '' : undefined}
      className="bg-ink-primary text-surface-raised font-numeric inline-flex shrink-0 items-center px-[6px] py-[1px] text-[11px] font-semibold leading-4"
    >
      <span aria-hidden className="tabular-nums">{formatNumber(count, i18n.language)}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}
